import { Navigate, Outlet, Route, Routes } from "react-router";

import { AppLayout } from "@/components/app-layout";
import { ErrorBoundary } from "@/components/error-boundary";
import { AdminRoute, GuestRoute, ProtectedRoute } from "@/components/protected-route";
import { AdminActivityPage } from "@/features/admin/activity";
import { AdminAiServicesPage } from "@/features/admin/ai-services";
import { AdminAppRegistrationsPage } from "@/features/admin/app-registrations";
import { AdminGroupsPage } from "@/features/admin/groups";
import { AdminItApprovalsPage } from "@/features/admin/it-approvals";
import { AdminProjectsPage } from "@/features/admin/projects";
import { AdminPromotionRequestsPage } from "@/features/admin/promotion-requests";
import {
  AdminServiceBudgetRequestsPage,
  ServicesGatewayOnly,
} from "@/features/admin/service-budget-requests";
import { AdminUsersPage } from "@/features/admin/users";
import { GitHubCallbackPage } from "@/features/github-callback";
import { HomePage } from "@/features/home";
import { HowToPage } from "@/features/how-to";
import { LoginPage } from "@/features/login";
import { NotFoundPage } from "@/features/not-found";
import { SignInApprovalResultPage } from "@/features/sign-in-approval";
import { YouPage } from "@/features/you";

export function App() {
  return (
    <Routes>
      {/* Top-level layout route purely to attach the error boundary to the
          whole tree (the SPA replacement for global-error.tsx). */}
      <Route ErrorBoundary={ErrorBoundary} element={<Outlet />}>
        <Route path="/" element={<Navigate to="/home" replace />} />

        {/* Public, for signed-in and signed-out visitors alike: where Entra's
            admin-consent prompt lands (via the backend's callback). The IT
            admin who consents may not be a Scaffold UI user. */}
        <Route path="/sign-in-approval" element={<SignInApprovalResultPage />} />

        {/* Public auth page — bounces to /home if already signed in. */}
        <Route element={<GuestRoute />}>
          <Route path="/login" element={<LoginPage />} />
        </Route>

        {/* Authenticated app. */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AppLayout />}>
            <Route path="/home" element={<HomePage />} />
            <Route path="/you" element={<YouPage />} />
            <Route path="/how-to" element={<HowToPage />} />
            <Route path="/how-to/:section" element={<HowToPage />} />
            <Route path="/github/callback" element={<GitHubCallbackPage />} />

            {/* Admin-only, nested inside the authenticated shell. */}
            <Route element={<AdminRoute />}>
              <Route path="/admin/users" element={<AdminUsersPage />} />
              <Route path="/admin/groups" element={<AdminGroupsPage />} />
              <Route path="/admin/projects" element={<AdminProjectsPage />} />
              <Route path="/admin/activity" element={<AdminActivityPage />} />
              <Route path="/admin/app-registrations" element={<AdminAppRegistrationsPage />} />
              <Route path="/admin/it-approvals" element={<AdminItApprovalsPage />} />
              <Route path="/admin/promotion-requests" element={<AdminPromotionRequestsPage />} />
              <Route
                path="/admin/ai-services"
                element={
                  <ServicesGatewayOnly>
                    <AdminAiServicesPage />
                  </ServicesGatewayOnly>
                }
              />
              <Route
                path="/admin/service-budget-requests"
                element={
                  <ServicesGatewayOnly>
                    <AdminServiceBudgetRequestsPage />
                  </ServicesGatewayOnly>
                }
              />
            </Route>
          </Route>
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
